// GENERATED FILE — do not edit by hand.
// Source: "Comparing Possible Plans" Google Sheet, tab "p(alignment) and likelihood estimates, all authors".
// Regenerate with: npm run sync-outcomes-by-author
// The plan labels, per-author colors, and helpers live in ./authorEstimatesData.ts.

import type { AuthorDataset } from './authorEstimatesData';

// Likelihood of roughly the given plan being attempted (cols F:K).
export const LIKELIHOOD: AuthorDataset = {
  "plans": [
    "a",
    "b",
    "cplus",
    "c",
    "d",
    "other"
  ],
  "authors": [
    {
      "author": "Eli",
      "values": {
        "a": 5,
        "b": 15,
        "cplus": 10,
        "c": 20,
        "d": 25,
        "other": 25
      },
      "reasoning": "p(govt does something at least as intense as Plan B): 35%; seems like govt wakeup has been increasing and also the importance of ASI will get more and more obvious. Though note that Plan B requires actually slowing down by at least 3 months.\np(deal | at least as intense on Plan B): 50%; deal is clearly rational but there are various blockers in practice. Again note that something at least as intense as Plan B requires at least a 3 month slowdown.\np(Plan A | deal): 30%; Plan S, a verified slowdown with less transparency, or just a somewhat weaker deal are all plausible.\nThis gives Plan A is ~5%, other deals are ~10%.\nLet’s say Plan B is 15% and other intense non-deal things are 5%, given that Plan B is quite broad.\nDomestic regulation as in Plan C+ seems plausible as well, though the requirement of actually having a 2 month slowdown lowers the probability. Also, it’s a bit of a narrow target to have the will to do this but not Plan B or higher.\nPlan C seems more likely than C+. Plan D seems a  bit more likely than Plan C.\nOther being at 25% feels maybe a bit low but not too bad as I think B, C, C+, and D together are broad enough to describe a bunch of the worlds that could happen at similar levels of intensity."
    },
    {
      "author": "Daniel",
      "values": {
        "a": 15,
        "b": 10,
        "cplus": 20,
        "c": 10,
        "d": 30,
        "other": 15
      },
      "reasoning": ""
    },
    {
      "author": "Thomas",
      "values": {
        "a": 3,
        "b": 8,
        "cplus": 8,
        "c": 12,
        "d": 50,
        "other": 19
      },
      "reasoning": ""
    },
    {
      "author": "Romeo",
      "values": {
        "a": 8,
        "b": 12,
        "cplus": 18,
        "c": 12,
        "d": 30,
        "other": 20
      },
      "reasoning": ""
    },
    {
      "author": "Ryan",
      "values": {
        "a": 4,
        "b": 12,
        "cplus": 25,
        "c": 14,
        "d": 28,
        "other": 18
      },
      "reasoning": "I’m very uncertain about what should count. I think Plan C+ is mostly worlds with dumb regulation that slows down AI. I think Plan B would be higher if not for the intent to spend the lead on safety."
    }
  ]
};

// p(alignment) = 1 − p(misaligned takeover) (cols O:T).
export const OUTCOMES: AuthorDataset = {
  "plans": [
    "a",
    "b",
    "cplus",
    "c",
    "d",
    "other"
  ],
  "authors": [
    {
      "author": "Eli",
      "values": {
        "a": 70,
        "b": 55,
        "cplus": 50,
        "c": 45,
        "d": 32,
        "other": 55
      },
      "reasoning": "My probabilities in the plan assessment above were conditional on:\n(a) the default trajectory being the [default Plan A one](https://www.ai-2040.com/supplements/takeoff-supplement), which has a bit under a year between AC and TED-AI and a year between AC and ASI.\n(b) The plan implementation beginning in early 2029, ~0.75 years before AC.\n\nMy median takeoff is ~2x longer than the one described in (a), but I have significant probability on faster takeoffs. This means that my p(alignment) estimates should do a combination of increase and regress a bit toward 50%. Also, Plan A isn’t as sensitive to default takeoff length as other plans, and from a covert project perspective it’s actually unclear in which way the effect size points because larger effective compute gaps between the start and end of takeoff require more software progress. The trajectory timeline to AC / start of takeoff is pretty similar to my median so for that I regress a bit toward 50%.\n\nFor (b), my median is that the plan gets implemented later than this, but I think it doesn’t matter much for all the plans besides A because in the other plans it’s best to slow down later anyway. I do lower the value of Plan A due to this consideration, though not hugely because I think covert projects would likely still be difficult to do without detection.\n\nAlso, the plan assessment numbers were based on the median implementation of the plan, rather than aggregating the expected outcome over all implementations. My unconfident quick guess is that switching to expected outcome should also regress my numbers toward 50%, but not sure so this adjustment will be small.\n\nWith the above in mind, I make the following adjustments:\nA: 72->70\nB: 50->55\nC+: 45->50\nC: 40->45\nD: 25->32\n\nFor Other, I think a bunch of this is random pretty good plans that aren’t covered, including Plan S. E.g. there’s ~40% that this is a non-Plan-A deal. Though it also includes Plan E. Overall I rate it the same as B, 55."
    },
    {
      "author": "Daniel",
      "values": {
        "a": 80,
        "b": 20,
        "cplus": 40,
        "c": 20,
        "d": 10,
        "other": 35
      },
      "reasoning": ""
    },
    {
      "author": "Thomas",
      "values": {
        "a": 80,
        "b": 53,
        "cplus": 45,
        "c": 35,
        "d": 20,
        "other": 30
      },
      "reasoning": ""
    },
    {
      "author": "Romeo",
      "values": {
        "a": 80,
        "b": 35,
        "cplus": 40,
        "c": 35,
        "d": 20,
        "other": 50
      },
      "reasoning": ""
    },
    {
      "author": "Ryan",
      "values": {
        "a": 81,
        "b": 68,
        "cplus": 60,
        "c": 63,
        "d": 55,
        "other": 50
      },
      "reasoning": "These numbers are very sensitive to how you classify plans. E.g., I think Plan A as described in the scenario has maybe 3/5 as much risk as Plan A as classified by the supplement. An actually good version of Plan C done by the leading AI company is pretty close to the numbers I give for Plan B (but worse than a great Plan B). A large fraction of “other” is worlds with not enough effort on safety to classify for Plan D."
    }
  ]
};
